import { useLocation } from '@provider/navigation';
import { scrollTo } from '@utils/common';
import { easeOut } from '@utils/constants';
import { useChangeEffect } from '@utils/react';
import { scrollDirectionStore } from './scroll-direction.js';

/**
 * Ensures that the user can see the top of the component every time the
 * current page is changed, which is very useful when the component is embedded
 * into other contexts.
 */
function useScrollIntoViewOnNavigation(targetRef, args) {
  const location = useLocation();
  useChangeEffect(() => {
    const target = targetRef.current;
    if (!target) {
      return;
    }
    const boundingRect = target.getBoundingClientRect();

    // Don't bother scrolling if the top of the embed target is clearly within
    // view.
    // NB: We do not bother checking lower bounds as the component cannot be
    // navigated when outside of the viewport as it will be inactive in this
    // state.
    if (boundingRect.top > 100) {
      return;
    }
    scrollTo(boundingRect.top + window.scrollY + (args?.offsetPx ?? 0), {
      minDurationMs: 375,
      maxDurationMs: 375,
      easingFn: p => easeOut(p, 3)
    });

    // Ensures that the header will remain collapsed when navigating between
    // task (required as navigation may case the browser to automatically
    // scroll upwards.)
    setTimeout(() => {
      const previouslyEnabled = scrollDirectionStore.getState().enabled;
      scrollDirectionStore.dispatch(scrollDirectionStore.actions.setDirection("down"));
      if (previouslyEnabled) {
        scrollDirectionStore.dispatch(scrollDirectionStore.actions.setState({
          enabled: false
        }));
        setTimeout(() => {
          scrollDirectionStore.dispatch(scrollDirectionStore.actions.setState({
            enabled: true
          }));
        }, 375);
      }
      // NB: We wait for a little while to determine if newly rendered content
      // explictly wants to handle header scroll direction, in case this
      // happens in effects that potentially run after this one.
    }, 50);
  }, [location.asPath, targetRef]);
}

export { useScrollIntoViewOnNavigation };
//# sourceMappingURL=scroll-into-view-on-navigation.js.map
